import React, { useState } from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    makeStyles,
    OutlinedInput,
    Checkbox,
    ListItemIcon,
    Box,
    Chip,
} from '@material-ui/core';
import { Block, Typography } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { theme } from '@klp/design/Theme/theme';
import { useGetRollerQuery } from '../services/rtkApi';
import type { BrukerRolle } from '../types';
import { useCurrentUser } from '@klp/auth';

const useStyles = makeStyles(() => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    chip: {
        padding: 1,
        border: '2px solid red',
        fontSize: '16px',
        borderColor: theme.palette.secondary.main,
        backgroundColor: 'white',
    },
    box: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    text: {
        paddingLeft: 8,
    },
    selectContainer: {
        '& .MuiSelect-selectMenu': {
            overflowY: 'scroll',
            maxHeight: '45px',
        },
    },
}));

interface RolleSelectorProps {
    rollerList?: Array<BrukerRolle>;
    hasEditAccess: boolean;
    brukerIsLoading?: boolean;
    callbackChange?: (value: Array<string>) => void;
}

export default function RolleSelector({
    rollerList,
    hasEditAccess,
    brukerIsLoading,
    callbackChange,
}: RolleSelectorProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { isEDAdmin } = useCurrentUser();

    const { data: rollerData = [], isLoading: rollerIsLoading } =
        useGetRollerQuery();

    const [selectedRoles, setSelectedRoles] = useState<Array<string>>(
        rollerList
            ?.filter((role) => Boolean(role.rolle))
            .map((role) => role.rolle || '') || [],
    );

    const [open, setOpen] = useState(false);

    if (brukerIsLoading || rollerIsLoading || !rollerData) {
        return <Block paddingBottom="tiny" />;
    }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleRolleSelect = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const value = event.target.value as Array<string>;
        setSelectedRoles(value);

        if (callbackChange) {
            callbackChange(value);
        }
    };

    const isDisabled = (name: string) =>
        name === 'Administrerendedirektor' && !isEDAdmin;

    return (
        <>
            {hasEditAccess ? (
                <Typography>{t('users.editUser.rolesDescription')}</Typography>
            ) : (
                <Typography>
                    {t('users.showUserInfo.rolesDescription')}
                </Typography>
            )}

            <Block paddingBottom="tiny" paddingTop="tiny">
                <FormControl className={classes.selector}>
                    <InputLabel
                        className={classes.text}
                        id="demo-multiple-checkbox-label"
                    >
                        {hasEditAccess
                            ? t('users.editUser.assignRoles')
                            : t('users.showUserInfo.labelRoles')}
                    </InputLabel>
                    <Select
                        disabled={!hasEditAccess}
                        className={classes.selectContainer}
                        labelId="demo-multiple-checkbox-label"
                        id="demo-multiple-checkbox"
                        multiple={true}
                        value={selectedRoles}
                        open={open}
                        onChange={handleRolleSelect}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        input={<OutlinedInput label="Tag" />}
                        renderValue={(selected: Array<string>) => (
                            <Box className={classes.box}>
                                {selected.map((value: string) => (
                                    <Chip
                                        className={classes.chip}
                                        key={value}
                                        label={t(
                                            `users.rolesSelector.${value}`,
                                        )}
                                    />
                                ))}
                            </Box>
                        )}
                    >
                        {rollerData.map((name) => (
                            <MenuItem
                                key={name}
                                value={name}
                                disabled={isDisabled(name)}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        checked={
                                            selectedRoles.indexOf(name) > -1
                                        }
                                    />
                                </ListItemIcon>
                                {t(`users.rolesSelector.${name}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Block>
        </>
    );
}
